import React, { useEffect, useState } from "react";
import { Button, Input, message, Select, Form, Image } from "antd";
import { authSVC } from "../../services";
import { Option } from "antd/es/mentions";
import { useB2BContext } from "../../context/ContextB2B";
import { useNavigate, useLocation } from "react-router-dom";
import "./EditEmployees.scss";

interface Contentdata {
  _id: string;
  full_name: string;
  email: string;
  user_type: string;
  company: string;
  department: string;
  gender: string;
  marital_Status: string;
  package_name: string;
}

function EditEmployees() {
  const [form] = Form.useForm();
  const { getUserByID, setAllPackageList, allPackageList } = useB2BContext();

  const [editContent, setEditContent] = useState<Contentdata>({
    _id: "", full_name: "", email: "", user_type: "",
    company: "", department: "", gender: "",
    marital_Status: "", package_name: "",
  });

  const [loading, setloading] = useState<boolean>(false);
  const [disableAddEmp, setDisableAddEmp] = useState(false);
  const [allDepartments, setAllDepartments] = useState<any>();
  const [packageList, setPackageList] = useState([]);
  const [packageId, setPackageId] = useState<any>();
  const [packageName, setPackageName] = useState<any>();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const UserId = queryParams.get("uid");

  useEffect(() => { getDepartments(getUserByID); }, [getUserByID]);
  useEffect(() => { setPackageList(getUserByID?.company_id?.package); }, [getUserByID]);

  const getDepartments = (getUserByID: any) => {
    if (getUserByID?.company) {
      authSVC.getCompanyDepartments(getUserByID?.company)
        .then((res: any) => setAllDepartments(res.data))
        .catch((err: any) => message.error(err.message));
    }
  };

  useEffect(() => {
    if (getUserByID?.email) {
      authSVC.getCompanyuser()
        .then((res: any) => {
          res.data?.data.map((data: any) => {
            if (data._id === UserId) {
              form.setFields([
                { name: "full_name", value: data?.full_name },
                { name: "department", value: data?.department },
                { name: "email", value: data?.email },
                { name: "selected_package", value: data?.package_name },
                { name: "gender", value: data?.gender },
                { name: "marital_Status", value: data?.marital_Status },
              ]);
              setEditContent((prev) => ({ ...prev, email: data?.email }));
            }
          });
        })
        .catch((err: any) => message.error(err.message));
    }
  }, [getUserByID, form]);

  useEffect(() => {
    if (allDepartments?.message?.length === 0) setDisableAddEmp(true);
    else setDisableAddEmp(false);
  }, [allDepartments]);

  const handleInputChange = (e: any) => {
    setEditContent((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSelectedDepratment = (value: any) => { /* intentional */ };
  const handleSelectedGender = (value: any) => { /* intentional */ };
  const handleSelectedMarital = (value: any) => { /* intentional */ };

  const handleSelectedPackage = (packageValue: any) => {
    const value = JSON.parse(packageValue);
    setPackageId(value._id);
    setPackageName(value.package);
  };

  const onFinish = (values: any) => {
    authSVC.updateEmployee(UserId, {
      full_name: values.full_name,
      email: values?.email,
      gender: values?.gender,
      marital_Status: values?.marital_Status,
      department: values?.department,
      package_id: packageId,
      package_name: packageName,
    })
      .then(() => {
        setloading(false);
        message.success("Employee updated successfully!");
        form.resetFields();
        navigate("/manage_exployees");
      })
      .catch((err: any) => {
        message.error(err.response?.data?.error);
        setloading(false);
      });
  };

  const nameValidation = (_: any, value: any) => {
    const regex = /^[A-Za-z\s]+$/;
    if (!value || regex.test(value)) return Promise.resolve();
    return Promise.reject(new Error("Please enter a valid name with only letters."));
  };

  return (
    <div className="edit-emp">
      {/* Header */}
      <div className="edit-emp__header">
        <div className="edit-emp__header-text">
          <button
            type="button"
            className="edit-emp__back"
            onClick={() => navigate("/manage_exployees")}
          >
            ← Back to Employees
          </button>
          <h1 className="edit-emp__title">Edit Employee</h1>
          <p className="edit-emp__subtitle">Update the employee's profile details below.</p>
        </div>
      </div>

      {/* No-dept warning */}
      {disableAddEmp && (
        <div className="edit-emp__warning">
          <span className="edit-emp__warning-icon">⚠️</span>
          <span>
            No departments found. Please <strong>create a department</strong> before editing employees.
          </span>
        </div>
      )}

      <Form
        name="edit_employee"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        form={form}
        className="edit-emp__form"
      >
        {/* ── Personal Details ──────────────────────────── */}
        <p className="edit-emp__section-label">Personal Details</p>

        <div className="edit-emp__field-group">
          <label className="edit-emp__label">
            <span className="edit-emp__label-icon">👤</span>
            Full Name
          </label>
          <Form.Item
            name="full_name"
            rules={[
              { required: true, message: "Please enter employee full name!" },
              { validator: nameValidation },
            ]}
          >
            <Input
              name="full_name"
              className="edit-emp__input"
              value={editContent?.full_name}
              onChange={handleInputChange}
              type="text"
              placeholder="e.g. Priya Sharma"
            />
          </Form.Item>
        </div>

        <div className="edit-emp__field-group">
          <label className="edit-emp__label">
            <span className="edit-emp__label-icon">✉️</span>
            Work Email
            <span className="edit-emp__badge">Read-only</span>
          </label>
          <Form.Item
            name="email"
            rules={[{ required: true, type: "email", message: "Please enter a valid email!" }]}
          >
            <Input
              className="edit-emp__input edit-emp__input--disabled"
              name="email"
              onChange={handleInputChange}
              value={editContent.email}
              disabled
              placeholder="employee@company.com"
            />
          </Form.Item>
        </div>

        {/* ── Package ───────────────────────────────────── */}
        <p className="edit-emp__section-label">Package</p>

        {packageList && (
          <div className="edit-emp__field-group">
            <label className="edit-emp__label">
              <span className="edit-emp__label-icon">📦</span>
              Employee Package
            </label>
            <Form.Item
              name="selected_package"
              rules={[{ required: true, message: "Please select employee package!" }]}
            >
              <Select
                placeholder="Select a package"
                onChange={handleSelectedPackage}
                className="edit-emp__select"
              >
                {packageList.map((packageDetail: any) => (
                  <Option
                    key={packageDetail.package_id._id}
                    value={JSON.stringify(packageDetail.package_id)}
                  >
                    {packageDetail.package_id.package}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}

        {/* ── Demographics ──────────────────────────────── */}
        <p className="edit-emp__section-label">Demographics</p>

        <div className="edit-emp__two-col">
          <div className="edit-emp__field-group">
            <label className="edit-emp__label">Gender</label>
            <Form.Item name="gender">
              <Select
                placeholder="Select gender"
                onChange={handleSelectedGender}
                className="edit-emp__select"
              >
                <Option value="Male">Male</Option>
                <Option value="Female">Female</Option>
                <Option value="Other">Other</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="edit-emp__field-group">
            <label className="edit-emp__label">Marital Status</label>
            <Form.Item name="marital_Status">
              <Select
                placeholder="Select status"
                onChange={handleSelectedMarital}
                className="edit-emp__select"
              >
                <Option value="Married">Married</Option>
                <Option value="UnMarried">Unmarried</Option>
              </Select>
            </Form.Item>
          </div>
        </div>

        <p className="edit-emp__section-label">Organization</p>

        <div className="edit-emp__field-group">
          <label className="edit-emp__label">
            <span className="edit-emp__label-icon">🏢</span>
            Department
          </label>
          <Form.Item name="department">
            <Select
              placeholder="Select department"
              onChange={handleSelectedDepratment}
              className="edit-emp__select"
            >
              {allDepartments?.data?.map((item: any, index: any) => (
                <Option value={item.name} key={index}>{item.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* ── Submit ────────────────────────────────────── */}
        <Button
          className="edit-emp__submit"
          htmlType="submit"
          loading={loading}
          disabled={disableAddEmp}
          icon={<Image src="/img/new/add.svg" alt="" preview={false} />}
        >
          Save Changes
        </Button>
      </Form>
    </div>
  );
}

export default EditEmployees;